import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from '@/lib/auth';
import { CartProvider } from '@/lib/cart';
import { initAnalytics, trackPageView } from '@/lib/analytics';
import { api } from '@/lib/api';
import RequireAuth from '@/components/RequireAuth';

import HomePage from '@/pages/HomePage';
import AboutPage from '@/pages/AboutPage';
import ServicesPage from '@/pages/ServicesPage';
import ServiceDetailPage from '@/pages/ServiceDetailPage';
import ResidentialPage from '@/pages/ResidentialPage';
import CommercialPage from '@/pages/CommercialPage';
import FinancingPage from '@/pages/FinancingPage';
import GalleryPage from '@/pages/GalleryPage';
import ReturnsPage from '@/pages/ReturnsPage';
import TrackOrderPage from '@/pages/TrackOrderPage';
import ReviewsPage from '@/pages/ReviewsPage';
import CareersPage from '@/pages/CareersPage';
import ContactPage from '@/pages/ContactPage';
const EstimateViewPage = lazy(() => import('@/pages/EstimateViewPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
import EstimateWizardPage from '@/pages/EstimateWizardPage';
import BookingPage from '@/pages/BookingPage';
import { ShopPage, ProductDetailPage } from '@/pages/ShopPages';
import CartPage from '@/pages/CartPage';
import CheckoutPage from '@/pages/CheckoutPage';

import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import CustomerPortalPage from '@/pages/CustomerPortalPage';
import CommercialPortalPage from '@/pages/CommercialPortalPage';
import { PaymentSuccessPage, PaymentCancelledPage } from '@/pages/PaymentResultPages';

// Admin section is lazy-loaded — public visitors never download this code,
// which keeps the shop/marketing bundle small and fast.
const AdminLayout = lazy(() => import('@/components/AdminLayout'));
const AdminHomePage = lazy(() => import('@/pages/admin/AdminHomePage'));
const AdminLeadsPage = lazy(() => import('@/pages/admin/AdminLeadsPage'));
const AdminLeadGenPage = lazy(() => import('@/pages/admin/AdminLeadGenPage'));
const AdminAutopilotPage = lazy(() => import('@/pages/admin/AdminAutopilotPage'));
const QualifyChatPage = lazy(() => import('@/pages/QualifyChatPage'));
const AdminCRMListPage = lazy(() => import('@/pages/admin/AdminCRMPage').then(m => ({ default: m.AdminCRMListPage })));
const AdminCRMDetailPage = lazy(() => import('@/pages/admin/AdminCRMPage').then(m => ({ default: m.AdminCRMDetailPage })));
const AdminPipelinePage = lazy(() => import('@/pages/admin/AdminPipelinePage'));
const AdminEstimatesListPage = lazy(() => import('@/pages/admin/AdminEstimatesListPage'));
const AdminEstimateBuilderPage = lazy(() => import('@/pages/admin/AdminEstimateBuilderPage'));
const AdminProjectsPage = lazy(() => import('@/pages/admin/AdminProjectsPage'));
const AdminCommercialPage = lazy(() => import('@/pages/admin/AdminCommercialPage'));
const AdminCalendarPage = lazy(() => import('@/pages/admin/AdminCalendarPage'));
const AdminBookingsPage = lazy(() => import('@/pages/admin/AdminBookingsPage'));
const AdminInvoicesPage = lazy(() => import('@/pages/admin/AdminInvoicesPage'));
const AdminPaymentsPage = lazy(() => import('@/pages/admin/AdminPaymentsPage'));
const AdminServicesPage = lazy(() => import('@/pages/admin/AdminServicesPage'));
const AdminProductsPage = lazy(() => import('@/pages/admin/AdminProductsPage'));
const AdminCJBrowsePage = lazy(() => import('@/pages/admin/AdminCJBrowsePage'));
const AdminOrdersPage = lazy(() => import('@/pages/admin/AdminOrdersPage'));
const AdminReturnsPage = lazy(() => import('@/pages/admin/AdminReturnsPage'));
const AdminChannelsPage = lazy(() => import('@/pages/admin/AdminChannelsPage'));
const AdminExpensesPage = lazy(() => import('@/pages/admin/AdminExpensesPage'));
const AdminMileagePage = lazy(() => import('@/pages/admin/AdminMileagePage'));
const AdminJobCostingPage = lazy(() => import('@/pages/admin/AdminJobCostingPage'));
const AdminPromotionsPage = lazy(() => import('@/pages/admin/AdminPromotionsPage'));
const AdminReviewsPage = lazy(() => import('@/pages/admin/AdminReviewsPage'));
const AdminGalleryPage = lazy(() => import('@/pages/admin/AdminGalleryPage'));
const AdminMessagesPage = lazy(() => import('@/pages/admin/AdminMessagesPage'));
const AdminHiringPage = lazy(() => import('@/pages/admin/AdminHiringPage'));
const AdminEmployeesPage = lazy(() => import('@/pages/admin/AdminEmployeesPage'));
const AdminWebsiteEditorPage = lazy(() => import('@/pages/admin/AdminWebsiteEditorPage'));
const AdminReportsPage = lazy(() => import('@/pages/admin/AdminReportsPage'));
const AdminSettingsPage = lazy(() => import('@/pages/admin/AdminSettingsPage'));
const AdminActivityLogPage = lazy(() => import('@/pages/admin/AdminActivityLogPage'));
const AdminAssistantPage = lazy(() => import('@/pages/admin/AdminAssistantPage'));
const AdminAffiliatesPage = lazy(() => import('@/pages/admin/AdminAffiliatesPage'));
const AdminAffiliateMaterialsPage = lazy(() => import('@/pages/admin/AdminAffiliateMaterialsPage'));
const AdminCampaignGeneratorPage = lazy(() => import('@/pages/admin/AdminCampaignGeneratorPage'));
const AdminMarketingHubPage = lazy(() => import('@/pages/admin/AdminMarketingHubPage'));
const AdminSocialPublisherPage = lazy(() => import('@/pages/admin/AdminSocialPublisherPage'));
const AdminLaunchKitPage = lazy(() => import('@/pages/admin/AdminLaunchKitPage'));
const AdminVideoStudioPage = lazy(() => import('@/pages/admin/AdminVideoStudioPage'));
const AdminCouponsPage = lazy(() => import('@/pages/admin/AdminCouponsPage'));
const AdminPriceBookPage = lazy(() => import('@/pages/admin/AdminPriceBookPage'));
// Consolidated admin hubs (decluttered sidebar)
const SalesHubPage = lazy(() => import('@/pages/admin/AdminHubs').then(m => ({ default: m.SalesHubPage })));
const AiLeadsHubPage = lazy(() => import('@/pages/admin/AdminHubs').then(m => ({ default: m.AiLeadsHubPage })));
const EstimatingHubPage = lazy(() => import('@/pages/admin/AdminHubs').then(m => ({ default: m.EstimatingHubPage })));
const ScheduleHubPage = lazy(() => import('@/pages/admin/AdminHubs').then(m => ({ default: m.ScheduleHubPage })));
const JobsHubPage = lazy(() => import('@/pages/admin/AdminHubs').then(m => ({ default: m.JobsHubPage })));
const FinanceHubPage = lazy(() => import('@/pages/admin/AdminHubs').then(m => ({ default: m.FinanceHubPage })));
const OrdersHubPage = lazy(() => import('@/pages/admin/AdminHubs').then(m => ({ default: m.OrdersHubPage })));
const ChannelsPromosHubPage = lazy(() => import('@/pages/admin/AdminHubs').then(m => ({ default: m.ChannelsPromosHubPage })));
const MarketingSuiteHubPage = lazy(() => import('@/pages/admin/AdminHubs').then(m => ({ default: m.MarketingHubPage })));
const ContentHubPage = lazy(() => import('@/pages/admin/AdminHubs').then(m => ({ default: m.ContentHubPage })));
const TeamHubPage = lazy(() => import('@/pages/admin/AdminHubs').then(m => ({ default: m.TeamHubPage })));
const InsightsHubPage = lazy(() => import('@/pages/admin/AdminHubs').then(m => ({ default: m.InsightsHubPage })));
import AffiliateLandingPage from '@/pages/AffiliateLandingPage';
import AffiliateApplyPage from '@/pages/AffiliateApplyPage';
import AffiliateLeaderboardPage from '@/pages/AffiliateLeaderboardPage';
import AffiliatePortalPage from '@/pages/AffiliatePortalPage';
import { captureRef } from '@/lib/affiliateRef';

// Fires a PageView to configured pixels on every SPA route change
function AnalyticsPageViews() {
  const location = useLocation();
  const first = React.useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; } // initial PageView fires at pixel init
    trackPageView();
  }, [location.pathname]);
  return null;
}

import '@/App.css';

// Capture ?ref=CODE into a 30-day cookie on any first page load
if (typeof window !== 'undefined') { try { captureRef(); } catch { /* ignore */ } }

export default function App() {
  // Load channel tracking pixels (Meta/GA4/TikTok/custom) once — shares the
  // cached /api/site request with the footer and other consumers.
  useEffect(() => {
    import('@/lib/site').then(({ getSite }) =>
      getSite().then(site => initAnalytics(site?.tracking))
    ).catch(() => {});
  }, []);

  // Copy/paste + interaction guard: dialog/dropdown libraries can leave the
  // page frozen (body stuck with pointer-events:none or user-select:none)
  // after an overlay closes, which blocks text selection, copying and
  // pasting everywhere until a reload. This watchdog clears the stuck
  // styles whenever no overlay is actually open.
  useEffect(() => {
    const unfreeze = () => {
      const overlayOpen = document.querySelector(
        '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"], ' +
        '[data-state="open"][role="menu"], [data-state="open"][role="listbox"], ' +
        '[data-radix-popper-content-wrapper]'
      );
      if (overlayOpen) return;
      const bodyStyle = document.body.style;
      if (bodyStyle.pointerEvents === 'none') bodyStyle.pointerEvents = '';
      if (bodyStyle.userSelect === 'none') bodyStyle.userSelect = '';
      if (bodyStyle.webkitUserSelect === 'none') bodyStyle.webkitUserSelect = '';
      const htmlStyle = document.documentElement.style;
      if (htmlStyle.pointerEvents === 'none') htmlStyle.pointerEvents = '';
      if (htmlStyle.userSelect === 'none') htmlStyle.userSelect = '';
      if (htmlStyle.webkitUserSelect === 'none') htmlStyle.webkitUserSelect = '';
    };
    const t = setInterval(unfreeze, 1200);
    return () => clearInterval(t);
  }, []);

  return (
    <AuthProvider>
      <CartProvider>
      <BrowserRouter>
        <AnalyticsPageViews />
        <Toaster position="top-right" richColors closeButton />
        <Suspense
          fallback={
            <div className="min-h-screen flex items-center justify-center bg-white">
              <div className="h-8 w-8 rounded-full border-2 border-[#17352D] border-t-transparent animate-spin" aria-label="Loading" />
            </div>
          }
        >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/:slug" element={<ServiceDetailPage />} />
          <Route path="/residential" element={<ResidentialPage />} />
          <Route path="/commercial" element={<CommercialPage />} />
          <Route path="/financing" element={<FinancingPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/estimate" element={<EstimateWizardPage />} />
          <Route path="/estimate/:token" element={<EstimateViewPage />} />
          <Route path="/qualify/:token" element={<QualifyChatPage />} />
          <Route path="/booking" element={<BookingPage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/returns" element={<ReturnsPage />} />
          <Route path="/track" element={<TrackOrderPage />} />
          <Route path="/shop/:id" element={<ProductDetailPage />} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />

          {/* Affiliate program (public) */}
          <Route path="/affiliate" element={<AffiliateLandingPage />} />
          <Route path="/affiliate/apply" element={<AffiliateApplyPage />} />
          <Route path="/affiliate/leaderboard" element={<AffiliateLeaderboardPage />} />
          <Route path="/affiliate-portal" element={<RequireAuth><AffiliatePortalPage /></RequireAuth>} />

          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/signup" element={<SignupPage />} />

          <Route path="/portal" element={<RequireAuth><CustomerPortalPage /></RequireAuth>} />
          <Route path="/commercial-portal" element={<RequireAuth role="commercial"><CommercialPortalPage /></RequireAuth>} />

          {/* Admin (nested under sidebar layout) */}
          <Route path="/admin" element={<RequireAuth role="admin"><AdminLayout /></RequireAuth>}>
            <Route index element={<AdminHomePage />} />
            <Route path="pipeline" element={<AdminPipelinePage />} />
            {/* Consolidated hubs (sidebar entries) — original routes below remain for deep links */}
            <Route path="sales" element={<SalesHubPage />} />
            <Route path="ai-leads" element={<AiLeadsHubPage />} />
            <Route path="estimating" element={<EstimatingHubPage />} />
            <Route path="schedule" element={<ScheduleHubPage />} />
            <Route path="jobs" element={<JobsHubPage />} />
            <Route path="finance" element={<FinanceHubPage />} />
            <Route path="orders-hub" element={<OrdersHubPage />} />
            <Route path="channels-promos" element={<ChannelsPromosHubPage />} />
            <Route path="marketing-hub" element={<MarketingSuiteHubPage />} />
            <Route path="content" element={<ContentHubPage />} />
            <Route path="team" element={<TeamHubPage />} />
            <Route path="insights" element={<InsightsHubPage />} />
            <Route path="leads" element={<AdminLeadsPage />} />
            <Route path="lead-generator" element={<AdminLeadGenPage />} />
            <Route path="autopilot" element={<AdminAutopilotPage />} />
            <Route path="crm" element={<AdminCRMListPage />} />
            <Route path="crm/:id" element={<AdminCRMDetailPage />} />
            <Route path="estimates" element={<AdminEstimatesListPage />} />
            <Route path="estimates/new" element={<AdminEstimateBuilderPage />} />
            <Route path="estimates/:id" element={<AdminEstimateBuilderPage />} />
            <Route path="projects" element={<AdminProjectsPage />} />
            <Route path="commercial" element={<AdminCommercialPage />} />
            <Route path="calendar" element={<AdminCalendarPage />} />
            <Route path="bookings" element={<AdminBookingsPage />} />
            <Route path="customers" element={<AdminCRMListPage />} />
            <Route path="invoices" element={<AdminInvoicesPage />} />
            <Route path="payments" element={<AdminPaymentsPage />} />
            <Route path="services" element={<AdminServicesPage />} />
            <Route path="products" element={<AdminProductsPage />} />
            <Route path="cj" element={<AdminCJBrowsePage />} />
            <Route path="orders" element={<AdminOrdersPage />} />
            <Route path="returns" element={<AdminReturnsPage />} />
            <Route path="channels" element={<AdminChannelsPage />} />
            <Route path="expenses" element={<AdminExpensesPage />} />
            <Route path="mileage" element={<AdminMileagePage />} />
            <Route path="job-costing" element={<AdminJobCostingPage />} />
            <Route path="promotions" element={<AdminPromotionsPage />} />
            <Route path="reviews" element={<AdminReviewsPage />} />
            <Route path="gallery" element={<AdminGalleryPage />} />
            <Route path="messages" element={<AdminMessagesPage />} />
            <Route path="hiring" element={<AdminHiringPage />} />
            <Route path="affiliates" element={<AdminAffiliatesPage />} />
            <Route path="affiliate-materials" element={<AdminAffiliateMaterialsPage />} />
            <Route path="marketing" element={<AdminMarketingHubPage />} />
            <Route path="campaigns" element={<AdminCampaignGeneratorPage />} />
            <Route path="social" element={<AdminSocialPublisherPage />} />
            <Route path="launch-kit" element={<AdminLaunchKitPage />} />
            <Route path="video-studio" element={<AdminVideoStudioPage />} />
            <Route path="coupons" element={<AdminCouponsPage />} />
            <Route path="price-book" element={<AdminPriceBookPage />} />
            <Route path="employees" element={<AdminEmployeesPage />} />
            <Route path="website" element={<AdminWebsiteEditorPage />} />
            <Route path="reports" element={<AdminReportsPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="activity" element={<AdminActivityLogPage />} />
            <Route path="assistant" element={<AdminAssistantPage />} />
          </Route>

          <Route path="*" element={<HomePage />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}
